/**
 * Kanban View - Drag-and-drop board for grouping records by field value
 */

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { ArrowLeft, LayoutGrid, List, ChevronDown, ChevronRight, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import { useSortable, SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { getSharedFkCollisionColumns } from "@/lib/myapp/shared-fk-collision";
import { myappApi } from "@/services/api/myapp";
import { useAuth } from "@/contexts/AuthContext";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import type { FormDetail, FormField, FormRecord, LinkedOption } from "@/types/myapp";

// Kanban mode is now designer-controlled via field.kanban_mode
// "drag" = interactive (cards movable), "view" = visual grouping only

// Note: Route types will be regenerated on next dev/build cycle
export const Route = createFileRoute("/portal/myapp/$tableId/kanban" as "/portal/myapp/$tableId/grid")({
  component: KanbanView,
});

// localStorage key for persisting group by field selection per table
const KANBAN_GROUP_BY_KEY = "kanban_group_by_";

function KanbanView() {
  const params = Route.useParams() as { tableId: string };
  const { tableId } = params;
  const { token } = useAuth();
  // Mirror the live token in a ref so the auto-running fetch effects below
  // aren't recreated when a silent JWT refresh rotates `token`. Were `token` in
  // their deps, a refresh would re-run them mid-session and flash the board's
  // loading state. Same idiom as the submit forms + grid (Layer 1). User-action
  // handlers (drag save) still read `token` directly — they run on demand.
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  const navigate = useNavigate();
  const formId = tableId;

  // Tier 4: Object-level permissions (shared hook — blocks render until resolved)
  const { permissions, loading: permsLoading, denied: permissionDenied } = useMyAppPermissions(formId);

  const [form, setForm] = useState<FormDetail | null>(null);
  const [records, setRecords] = useState<FormRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Restore groupByFieldId from localStorage if available
  const [groupByFieldId, setGroupByFieldId] = useState<string | null>(() => {
    const stored = localStorage.getItem(`${KANBAN_GROUP_BY_KEY}${formId}`);
    return stored || null;
  });
  const [activeCard, setActiveCard] = useState<FormRecord | null>(null);
  // Mobile view mode: 'board' for horizontal columns, 'list' for collapsible vertical list
  const [viewMode, setViewMode] = useState<'board' | 'list'>('board');
  // Track expanded columns in list view
  const [expandedColumns, setExpandedColumns] = useState<Set<string>>(new Set());
  const [linkedOptions, setLinkedOptions] = useState<LinkedOption[]>([]);
  // Track in-flight drag operations to prevent refresh races
  const pendingDragOps = useRef(0);

  // Persist groupByFieldId to localStorage when it changes
  useEffect(() => {
    if (groupByFieldId) {
      localStorage.setItem(`${KANBAN_GROUP_BY_KEY}${formId}`, String(groupByFieldId));
    }
  }, [formId, groupByFieldId]);

  // DnD sensors - TouchSensor with delay to allow scrolling on mobile
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,     // 200ms press before drag starts
        tolerance: 5,   // 5px movement tolerance during delay
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Get eligible fields for grouping — only fields with kanban_mode set
  const eligibleFields = useMemo(() => {
    if (!form) return [];
    return form.fields.filter((f) => f.kanban_mode !== null && !f.is_hidden);
  }, [form]);

  // Whether the currently selected group-by field allows drag
  const isDragEnabled = useMemo(() => {
    if (!groupByFieldId || !form) return false;
    const field = form.fields.find((f) => f.id === groupByFieldId);
    return field?.kanban_mode === "drag";
  }, [groupByFieldId, form]);

  // Selected grouping field
  const groupByField = useMemo(() => {
    if (!groupByFieldId || !form) return null;
    return form.fields.find((f) => f.id === groupByFieldId) || null;
  }, [groupByFieldId, form]);

  // Shared-FK collision link columns. Dragging a card writes the group-by
  // field on an existing record, so if that field (or a lookup proxy's source
  // link) collides, the drag would corrupt the shared FK — block it. Tier 1.
  const sharedFkCollisionCols = useMemo(
    () => getSharedFkCollisionColumns(form?.fields ?? []),
    [form?.fields],
  );

  // Group records by field value
  const groupedRecords = useMemo(() => {
    if (!groupByField || !records.length) return new Map<string, FormRecord[]>();

    const groups = new Map<string, FormRecord[]>();
    const columnName = groupByField.column_name;
    const isLinkField = groupByField.field_type?.code === "link";
    const isBitField = groupByField.field_type?.code === "bit";

    for (const record of records) {
      // For link fields, use the _display column for grouping key
      const rawValue = isLinkField
        ? record[`${columnName}_display`] ?? record[columnName]
        : record[columnName];
      let key: string;

      if (isBitField) {
        key = rawValue === true || rawValue === 1 ? "Yes" : "No";
      } else if (rawValue != null && rawValue !== "") {
        key = String(rawValue);
      } else {
        key = "__unset__";
      }

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(record);
    }

    return groups;
  }, [records, groupByField]);

  // Get display fields for cards (first 3 visible non-longtext fields)
  const displayFields = useMemo(() => {
    if (!form) return [];
    return form.fields
      .filter(
        (f) =>
          !f.is_hidden &&
          f.field_type?.code !== "longtext" &&
          f.id !== groupByFieldId
      )
      .slice(0, 3);
  }, [form, groupByFieldId]);

  // Reusable function to refresh records
  const refreshRecords = useCallback(async () => {
    if (!form?.is_published) return;
    try {
      const allRecords = await myappApi.records.listAll(tokenRef.current, formId);
      setRecords(allRecords);
    } catch (err) {
      console.error("Failed to refresh records:", err);
    }
    // `token` read via tokenRef.current; omitted from deps so a JWT refresh
    // doesn't churn this callback's identity.

  }, [formId, form?.is_published]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const formData = await myappApi.tables.get(tokenRef.current, formId);
        setForm(formData);

        if (formData.is_published) {
          const allRecords = await myappApi.records.listAll(tokenRef.current, formId);
          setRecords(allRecords);
        }

        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // `token` intentionally omitted — read via tokenRef.current so a silent JWT
    // refresh doesn't re-run this and flash the board's loading state.

  }, [formId]);

  // Auto-select first eligible field
  useEffect(() => {
    if (eligibleFields.length > 0 && !groupByFieldId) {
      setGroupByFieldId(eligibleFields[0].id);
    }
  }, [eligibleFields, groupByFieldId]);

  // Fetch linked options when a link or lookup-proxy field is selected for grouping
  useEffect(() => {
    const isLink = groupByField?.field_type?.code === "link";
    const isLookupProxy = groupByField?.field_type?.code === "lookup" && groupByField?.source_link_field_id;

    if (!groupByField || (!isLink && !isLookupProxy)) {
      setLinkedOptions([]);
      return;
    }

    const fetchOptions = async () => {
      // For lookup proxy, resolve the source link field to get its linked table
      let linkedTableId: string | null = null;
      let displayFieldId: string | null = null;

      if (isLookupProxy && form) {
        const sourceLink = form.fields.find((f) => f.id === groupByField.source_link_field_id);
        linkedTableId = sourceLink?.linked_table_id ?? null;
        displayFieldId = sourceLink?.linked_display_field_id ?? null;
      } else {
        linkedTableId = groupByField.linked_table_id;
        displayFieldId = groupByField.linked_display_field_id;
      }

      if (!linkedTableId) return;

      try {
        const options = await myappApi.records.getLinkedOptions(
          tokenRef.current,
          formId,
          linkedTableId,
          { displayFieldId: displayFieldId || undefined }
        );
        setLinkedOptions(options);
      } catch (err) {
        console.error("Failed to fetch linked options for Kanban:", err);
        setLinkedOptions([]);
      }
    };

    fetchOptions();
    // `token` read via tokenRef.current; omitted so a JWT refresh doesn't re-run this.

  }, [formId, groupByField, form]);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const record = records.find((r) => getPkValue(r) === active.id);
    setActiveCard(record || null);
  };

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveCard(null);

      if (!over || !groupByField || !form) return;

      // View-only fields don't support drag
      if (groupByField.kanban_mode !== "drag") return;

      const recordId = active.id as number;

      // dnd-kit reports over.id as the column droppable's string id when dropped
      // on empty column space, or a card's numeric sortable id when stacked onto
      // another card. Without this resolution, stacking sends a card-id as the
      // new link value, triggering an FK violation on the server.
      let newColumnKey: string;
      if (typeof over.id === "number") {
        let resolved: string | null = null;
        for (const [key, groupRecords] of groupedRecords.entries()) {
          if (groupRecords.some((r) => getPkValue(r) === over.id)) {
            resolved = key;
            break;
          }
        }
        if (!resolved) return;
        newColumnKey = resolved;
      } else {
        newColumnKey = over.id;
      }

      // Lookup proxy: if grouping by a lookup field, resolve the source link field
      // so the API update targets the FK, not the read-only lookup column
      const isLookupProxy = groupByField.field_type?.code === "lookup" && groupByField.source_link_field_id;
      const sourceLinkField = isLookupProxy
        ? form.fields.find((f) => f.id === groupByField.source_link_field_id)
        : null;

      // Shared-FK collision guard: the column this drag would write (the link
      // group-by field, or a lookup proxy's source link field). If it shares a
      // physical FK column with another link field, writing it on this existing
      // record would overwrite the good snapshot with the shared FK. Pause the
      // move rather than corrupt. Tier 1 "stop the bleed".
      const dragWriteColumn = (sourceLinkField ?? groupByField).column_name;
      if (sharedFkCollisionCols.has(dragWriteColumn)) {
        toast.info(
          "This board groups by a shared multi-lookup field that's under review — moving cards is paused for now.",
        );
        return;
      }

      // Determine the new value based on the column key and field type
      let newValue: unknown;
      let newDisplayValue: string | null = null;
      const effectiveField = sourceLinkField || groupByField;
      const isLinkField = effectiveField.field_type?.code === "link" || isLookupProxy;
      const isBitField = effectiveField.field_type?.code === "bit";

      if (newColumnKey === "__unset__") {
        newValue = null;
        newDisplayValue = null;
      } else if (isBitField) {
        newValue = newColumnKey === "Yes";
      } else if (isLinkField) {
        // For link fields, find the option by display value and use its ID
        const option = linkedOptions.find((opt) => opt.display_value === newColumnKey);
        if (option) {
          newValue = option.id;
          newDisplayValue = option.display_value;
        } else {
          // Fallback: use the column key as-is (shouldn't happen if linkedOptions is complete)
          newValue = newColumnKey;
        }
      } else {
        newValue = newColumnKey;
      }

      const currentRecord = records.find((r) => getPkValue(r) === recordId);
      if (!currentRecord) return;

      // For link fields, use _fk column for FK comparison; the main column has the display value
      const currentValue = isLinkField
        ? currentRecord[`${groupByField.column_name}_fk`] ?? currentRecord[groupByField.column_name]
        : currentRecord[groupByField.column_name];
      const currentDisplayValue = currentRecord[`${groupByField.column_name}_display`];

      // Skip if dropped in same column
      if (currentValue === newValue) return;
      if (
        (currentValue == null || currentValue === "") &&
        newColumnKey === "__unset__"
      )
        return;

      // Optimistic update (update both raw value and _display for link fields)
      setRecords((prev) =>
        prev.map((r) => {
          if (getPkValue(r) !== recordId) return r;
          const updated = { ...r };
          if (isLinkField) {
            updated[`${groupByField.column_name}_fk`] = newValue;
            updated[`${groupByField.column_name}_display`] = newDisplayValue;
            updated[groupByField.column_name] = newDisplayValue ?? String(newValue);
          } else {
            updated[groupByField.column_name] = newValue;
          }
          return updated;
        })
      );

      // API update — for lookup proxy, update the source link field instead
      const updateColumnName = sourceLinkField?.column_name ?? groupByField.column_name;
      const updateLinkedTable = sourceLinkField?.linked_table ?? groupByField.linked_table;
      pendingDragOps.current += 1;
      try {
        await myappApi.records.update(token, formId, recordId, {
          [updateColumnName]: isLinkField ? (newDisplayValue ?? newValue) : newValue,
          ...(isLinkField && updateLinkedTable?.primary_key_column
            ? { [updateLinkedTable.primary_key_column]: newValue }
            : {}),
        });
        toast.success("Record updated");
        // Only refresh from server when no other drags are in-flight,
        // otherwise a stale server response would overwrite a newer optimistic update
        pendingDragOps.current -= 1;
        if (pendingDragOps.current === 0) {
          await refreshRecords();
        }
      } catch (err) {
        pendingDragOps.current -= 1;
        // Revert on error
        setRecords((prev) =>
          prev.map((r) => {
            if (getPkValue(r) !== recordId) return r;
            const reverted = { ...r };
            if (isLinkField) {
              reverted[`${groupByField.column_name}_fk`] = currentValue;
              reverted[`${groupByField.column_name}_display`] = currentDisplayValue;
              reverted[groupByField.column_name] = currentDisplayValue ?? String(currentValue);
            } else {
              reverted[groupByField.column_name] = currentValue;
            }
            return reverted;
          })
        );
        toast.error("Failed to update record");
        console.error("Kanban update failed:", err);
        // If this was the last in-flight op, refresh to reconcile state
        if (pendingDragOps.current === 0) {
          await refreshRecords();
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formId, token, groupByField, form, records, linkedOptions, refreshRecords, groupedRecords, sharedFkCollisionCols]
  );

  const getPkValue = (record: FormRecord): number | null => {
    if (!form) return null;
    const pk = record[form.primary_key_column];
    return typeof pk === "number" ? pk : null;
  };

  const formatDisplayValue = (value: unknown, field: FormField, maxLength = 50): string => {
    let result: string;

    if (value === null || value === undefined) return "—";
    if (typeof value === "boolean") {
      result = value ? "Yes" : "No";
    } else if (typeof value === "number") {
      if (field.decimal_places !== null && field.decimal_places !== undefined) {
        result = value.toFixed(field.decimal_places);
      } else {
        result = String(value);
      }
    } else {
      result = String(value);
    }

    // Truncate to maxLength
    if (result.length > maxLength) {
      return result.slice(0, maxLength) + "…";
    }
    return result;
  };

  // Opening a card reuses the table view's edit route — single source of truth
  // for record editing (renderField, attachments, signatures, cascading dropdowns,
  // inherited fields, calc/concat/auto-number read-onlys, header-detail composites).
  const openRecord = (record: FormRecord) => {
    const pk = getPkValue(record);
    if (!pk) return;
    navigate({
      to: "/portal/myapp/$tableId/submit",
      params: { tableId: String(formId) },
      search: { edit: pk, from: "kanban" },
    });
  };

  if (loading || permsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (permissionDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to view this table."
        backTo={`/portal/myapp/${tableId}`}
        backLabel="Back to Table"
      />
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-4">
        <p className="text-muted-foreground">Table not found</p>
      </div>
    );
  }

  if (!form.is_published) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              This table must be published before you can use Kanban view.
            </p>
            <Link to="/portal/myapp/$tableId/edit" params={{ tableId: String(formId) }}>
              <Button className="mt-4">Go to Table Settings</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (eligibleFields.length === 0) {
    return (
      <div className="p-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              No fields are configured for Kanban view. Enable Kanban mode on
              a field in the table editor to use this view.
            </p>
            <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }}>
              <Button className="mt-4" variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Table
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get column keys - for link fields, use all linked options; for others, use existing values
  const isLinkField = groupByField?.field_type?.code === "link";
  let columnKeys: string[];

  if (isLinkField && linkedOptions.length > 0) {
    // Use all linked options as columns (so users can drag to any option)
    columnKeys = linkedOptions.map((opt) => opt.display_value);
    // Also add "Unset" column at the end
    columnKeys.push("__unset__");
  } else {
    // For text/bit fields, use unique values from records
    columnKeys = Array.from(groupedRecords.keys()).sort((a, b) => {
      if (a === "__unset__") return 1;
      if (b === "__unset__") return -1;
      return a.localeCompare(b);
    });

    // Ensure "Unset" column exists
    if (!columnKeys.includes("__unset__")) {
      columnKeys.push("__unset__");
    }
  }

  // Toggle column expansion in list view
  const toggleColumnExpanded = (key: string) => {
    setExpandedColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="p-2 sm:p-4 w-full max-w-full overflow-hidden">
      {/* Header - responsive layout */}
      <div className="flex flex-col gap-4 mb-4 sm:mb-6">
        {/* Top row: Back button, title, view toggle (mobile) */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/portal/myapp/$tableId" params={{ tableId: String(formId) }}>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
                <LayoutGrid className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="truncate max-w-[150px] sm:max-w-none">{form.name}</span>
                <span className="hidden sm:inline">- Kanban</span>
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                {records.length} record{records.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          {/* Mobile view toggle */}
          <div className="flex items-center gap-1 md:hidden">
            <Button
              variant={viewMode === 'board' ? 'default' : 'outline'}
              size="icon"
              className="h-9 w-9"
              onClick={() => setViewMode('board')}
              title="Board view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="icon"
              className="h-9 w-9"
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Field selector + Add Record button - wraps on mobile */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Group by:</span>
            <Select
              value={groupByFieldId ? String(groupByFieldId) : ""}
              onValueChange={(v) => setGroupByFieldId(v)}
            >
              <SelectTrigger className="w-full sm:w-[200px]">
                <SelectValue placeholder="Select field" />
              </SelectTrigger>
              <SelectContent>
                {eligibleFields.map((field) => (
                  <SelectItem key={field.id} value={String(field.id)}>
                    {field.name}
                    {field.kanban_mode === "view" ? " (view only)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {form.table_type !== "virtual" && permissions?.can_submit && (
            form.needs_republish ? (
              <Button disabled title="Table design has been changed — contact your administrator to re-publish">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            ) : form.is_locked ? (
              <Button disabled title="Table is locked">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            ) : (
              <Link
                to="/portal/myapp/$tableId/submit"
                params={{ tableId: String(formId) }}
                search={{ from: "kanban" }}
              >
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Record
                </Button>
              </Link>
            )
          )}
        </div>
      </div>

      {/* Kanban Board - Board View (default, always on desktop) */}
      {(viewMode === 'board' || window.innerWidth >= 768) && (
        <DndContext
          sensors={isDragEnabled ? sensors : []}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className={cn(
            "flex gap-2 sm:gap-4 overflow-x-auto pb-4 w-full",
            "snap-x snap-mandatory md:snap-none", // Scroll snap on mobile only
            "hidden md:flex", // Hide on mobile when in list mode
            viewMode === 'board' && "flex" // Show on mobile when in board mode
          )}>
            {columnKeys.map((key) => (
              <KanbanColumn
                key={key}
                id={key}
                title={key === "__unset__" ? "Unset" : key}
                records={groupedRecords.get(key) || []}
                form={form}
                displayFields={displayFields}
                getPkValue={getPkValue}
                formatDisplayValue={formatDisplayValue}
                onCardClick={openRecord}
              />
            ))}
          </div>

          {/* Drag overlay */}
          <DragOverlay>
            {activeCard && (
              <KanbanCardContent
                record={activeCard}
                form={form}
                displayFields={displayFields}
                formatDisplayValue={formatDisplayValue}
                isDragging
              />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {/* Mobile List View - Collapsible columns */}
      {viewMode === 'list' && (
        <div className="space-y-3 md:hidden">
          {columnKeys.map((key) => {
            const columnRecords = groupedRecords.get(key) || [];
            const isExpanded = expandedColumns.has(key);
            const title = key === "__unset__" ? "Unset" : key;

            return (
              <div key={key} className="bg-muted/50 rounded-lg overflow-hidden">
                {/* Column header - tap to expand/collapse */}
                <button
                  onClick={() => toggleColumnExpanded(key)}
                  className="w-full flex items-center justify-between p-3 hover:bg-muted/80 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    <span className="font-medium text-sm">{title}</span>
                  </div>
                  <Badge variant="secondary">{columnRecords.length}</Badge>
                </button>

                {/* Expanded cards */}
                {isExpanded && columnRecords.length > 0 && (
                  <div className="p-2 pt-0 space-y-2">
                    {columnRecords.map((record) => (
                      <div
                        key={getPkValue(record)}
                        onClick={() => openRecord(record)}
                        className="cursor-pointer"
                      >
                        <KanbanCardContent
                          record={record}
                          form={form}
                          displayFields={displayFields}
                          formatDisplayValue={formatDisplayValue}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Empty state */}
                {isExpanded && columnRecords.length === 0 && (
                  <p className="p-3 pt-0 text-sm text-muted-foreground">
                    No records
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}

// Kanban Column Component
interface KanbanColumnProps {
  id: string;
  title: string;
  records: FormRecord[];
  form: FormDetail;
  displayFields: FormField[];
  getPkValue: (record: FormRecord) => number | null;
  formatDisplayValue: (value: unknown, field: FormField) => string;
  onCardClick: (record: FormRecord) => void;
}

function KanbanColumn({
  id,
  title,
  records,
  form,
  displayFields,
  getPkValue,
  formatDisplayValue,
  onCardClick,
}: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "flex-shrink-0 w-64 sm:w-72 md:w-80 bg-muted/50 rounded-lg p-2 sm:p-3",
        "min-h-[300px] sm:min-h-[400px]",
        "snap-start", // Scroll snap alignment
        isOver && "ring-2 ring-primary/50 bg-primary/5"
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-sm truncate" title={title}>
          {title}
        </h3>
        <Badge variant="secondary">{records.length}</Badge>
      </div>
      <SortableContext
        items={records.map((r) => getPkValue(r) || 0)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-2 min-h-[100px]">
          {records.map((record) => (
            <KanbanCard
              key={getPkValue(record)}
              record={record}
              form={form}
              displayFields={displayFields}
              getPkValue={getPkValue}
              formatDisplayValue={formatDisplayValue}
              onClick={() => onCardClick(record)}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

// Kanban Card Component (draggable)
interface KanbanCardProps {
  record: FormRecord;
  form: FormDetail;
  displayFields: FormField[];
  getPkValue: (record: FormRecord) => number | null;
  formatDisplayValue: (value: unknown, field: FormField) => string;
  onClick: () => void;
}

function KanbanCard({
  record,
  form,
  displayFields,
  getPkValue,
  formatDisplayValue,
  onClick,
}: KanbanCardProps) {
  const recordId = getPkValue(record) || 0;
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: recordId,
  });

  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
      }
    : undefined;

  // Handle click - only trigger if not dragging
  const handleClick = () => {
    // Prevent click during or after drag
    if (isDragging) return;
    onClick();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={cn(
        "cursor-grab active:cursor-grabbing",
        isDragging && "opacity-50"
      )}
    >
      <KanbanCardContent
        record={record}
        form={form}
        displayFields={displayFields}
        formatDisplayValue={formatDisplayValue}
      />
    </div>
  );
}

// Card content (shared between card and drag overlay)
interface KanbanCardContentProps {
  record: FormRecord;
  form: FormDetail;
  displayFields: FormField[];
  formatDisplayValue: (value: unknown, field: FormField) => string;
  isDragging?: boolean;
}

function KanbanCardContent({
  record,
  form,
  displayFields,
  formatDisplayValue,
  isDragging,
}: KanbanCardContentProps) {
  return (
    <Card
      className={cn(
        "shadow-sm hover:shadow-md transition-shadow",
        isDragging && "shadow-lg ring-2 ring-primary"
      )}
    >
      <CardContent className="p-3">
        {/* Primary key badge */}
        <div className="flex justify-between items-start mb-2">
          <Badge variant="outline" className="text-xs">
            #{String(record[form.primary_key_column])}
          </Badge>
        </div>

        {/* Display fields */}
        {displayFields.map((field) => {
          // Use _display column for link fields
          const value = field.field_type?.code === "link"
            ? record[`${field.column_name}_display`] ?? record[field.column_name]
            : record[field.column_name];
          return (
            <div key={field.id} className="text-sm mb-1 last:mb-0">
              <span className="text-muted-foreground">{field.name}:</span>{" "}
              <span className="font-medium break-words">
                {formatDisplayValue(value, field)}
              </span>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
